import * as Sentry from "@sentry/react"
import type { AxiosError } from "axios"
import { PROMOTIONS_API_PREFIX } from "@/shared/lib/axios/axios.constants"

const FALLBACK_ORIGIN = "https://redacted.invalid"
const LONG_NUMERIC_SEGMENT = /^\d{10,}$/
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const LONG_HEX_SEGMENT = /^[0-9a-f]{20,}$/i
const LONG_TOKEN_SEGMENT = /^[a-z\d_-]{32,}$/i
const EMAIL_PATH_SEGMENT = /@|%40/i
const SENSITIVE_DYNAMIC_ROUTE_PREFIXES = [PROMOTIONS_API_PREFIX]

const redactKnownDynamicSegments = (pathname: string) =>
  SENSITIVE_DYNAMIC_ROUTE_PREFIXES.reduce((redactedPath, routePrefix) => {
    if (!redactedPath.startsWith(`${routePrefix}/`)) return redactedPath

    const remainingPath = redactedPath.slice(routePrefix.length + 1)
    const nextSeparator = remainingPath.indexOf("/")
    const trailingPath =
      nextSeparator === -1 ? "" : remainingPath.slice(nextSeparator)

    return `${routePrefix}/:id${trailingPath}`
  }, pathname)

const redactPathIdentifiers = (pathname: string) =>
  redactKnownDynamicSegments(pathname)
    .split("/")
    .map((segment) =>
      LONG_NUMERIC_SEGMENT.test(segment) ||
      UUID_SEGMENT.test(segment) ||
      LONG_HEX_SEGMENT.test(segment) ||
      LONG_TOKEN_SEGMENT.test(segment) ||
      EMAIL_PATH_SEGMENT.test(segment)
        ? ":id"
        : segment
    )
    .join("/")

export const sanitizeNetworkRequestTarget = (
  requestUrl?: string,
  baseUrl?: string
) => {
  if (!requestUrl) return undefined

  try {
    const url = new URL(requestUrl, baseUrl ?? FALLBACK_ORIGIN)
    const pathname = redactPathIdentifiers(url.pathname)
    const hasKnownOrigin =
      Boolean(baseUrl) || /^[a-z][a-z\d+.-]*:\/\//i.test(requestUrl)

    return hasKnownOrigin ? `${url.origin}${pathname}` : pathname
  } catch {
    const pathOnly = requestUrl.split(/[?#]/, 1)[0]
    return redactPathIdentifiers(pathOnly)
  }
}

const removeUndefined = (values: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined)
  )

export const buildAxiosErrorBreadcrumbData = (error: AxiosError) =>
  removeUndefined({
    request_method: error.config?.method?.toUpperCase(),
    request_url: sanitizeNetworkRequestTarget(
      error.config?.url,
      error.config?.baseURL
    ),
    status_code: error.response?.status,
    error_code: error.code,
    timeout_ms: error.config?.timeout,
  })

export const addAxiosErrorBreadcrumb = (error: AxiosError) => {
  Sentry.addBreadcrumb({
    category: "http.client",
    type: "http",
    level:
      error.response?.status !== undefined && error.response.status < 500
        ? "warning"
        : "error",
    message: "HTTP request failed",
    data: buildAxiosErrorBreadcrumbData(error),
  })
}

interface EventSourceLike {
  readyState: number
  url: string
}

interface EventSourceErrorLike {
  status?: number
}

export const buildEventSourceErrorBreadcrumbData = ({
  eventSource,
  event,
  stream,
}: {
  eventSource: EventSourceLike
  event: EventSourceErrorLike
  stream: string
}) =>
  removeUndefined({
    stream,
    request_url: sanitizeNetworkRequestTarget(eventSource.url),
    status_code: event.status,
    ready_state: eventSource.readyState,
  })

export const addEventSourceErrorBreadcrumb = ({
  eventSource,
  event,
  stream,
}: {
  eventSource: EventSourceLike
  event: EventSourceErrorLike
  stream: string
}) => {
  Sentry.addBreadcrumb({
    category: "sse.connection",
    type: "http",
    level:
      event.status !== undefined && event.status >= 400 ? "error" : "warning",
    message: "EventSource connection error",
    data: buildEventSourceErrorBreadcrumbData({ eventSource, event, stream }),
  })
}
